import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiTool, FiShield, FiClock, FiHeart } = FiIcons;

const About = () => {
  useEffect(() => {
    document.title = "About AutoFix Pro - Professional Car Repair Services | Expert Auto Maintenance";
  }, []);

  const stats = [
    { icon: FiUsers, number: '500+', label: 'Happy Customers' },
    { icon: FiAward, number: '15+', label: 'Years Experience' },
    { icon: FiTool, number: '1000+', label: 'Repairs Completed' },
    { icon: FiShield, number: '100%', label: 'Satisfaction Rate' }
  ];

  const team = [
    {
      name: 'Mike Johnson',
      position: 'Master Technician',
      experience: '15+ Years',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face'
    },
    {
      name: 'Sarah Davis',
      position: 'Engine Specialist',
      experience: '12+ Years',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face'
    },
    {
      name: 'Tom Rodriguez',
      position: 'Brake & Suspension Expert',
      experience: '10+ Years',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face'
    }
  ];

  const values = [
    {
      icon: FiShield,
      title: 'Integrity',
      description: 'We believe in honest, transparent service with no hidden fees or unnecessary repairs.'
    },
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'Our certified technicians deliver top-quality workmanship on every job.'
    },
    {
      icon: FiHeart,
      title: 'Customer Care',
      description: 'Your satisfaction is our priority. We treat every car like our own.'
    },
    {
      icon: FiClock,
      title: 'Reliability',
      description: 'Fast, efficient service without compromising on quality or safety.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold text-black mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6">
                AutoFix Pro was founded in 2008 by Mike Johnson, a master technician with a passion 
                for automotive excellence. What started as a small garage has grown into the city's 
                most trusted auto repair shop, serving hundreds of satisfied customers.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our mission is simple: provide honest, reliable, and professional automotive services 
                at fair prices. We believe in building long-term relationships with our customers 
                through exceptional service and transparent communication.
              </p>
              <p className="text-lg text-gray-600">
                Today, our team of ASE-certified technicians continues to uphold the highest standards 
                of automotive repair, using state-of-the-art equipment and genuine parts to ensure 
                your vehicle runs safely and efficiently.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=600&h=400&fit=crop"
                alt="AutoFix Pro garage"
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Our Achievements</h2>
            <p className="text-xl max-w-2xl mx-auto">
              Numbers that speak to our commitment to excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="text-2xl" />
                </div>
                <div className="text-3xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-black mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-lg shadow-md"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="text-red-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-black mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Certified professionals dedicated to keeping your vehicle running smoothly
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-gray-50 p-6 rounded-lg"
              >
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-black mb-2">{member.name}</h3>
                <p className="text-red-600 font-medium mb-2">{member.position}</p>
                <p className="text-gray-600">{member.experience}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Certifications & Guarantees</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Our commitment to quality is backed by industry certifications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center bg-gray-800 p-6 rounded-lg"
            >
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-3">ASE Certified</h3>
              <p className="text-gray-300">All our technicians are certified by the National Institute for Automotive Service Excellence</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center bg-gray-800 p-6 rounded-lg"
            >
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-3">12-Month Warranty</h3>
              <p className="text-gray-300">We stand behind our work with a comprehensive 12-month warranty on all repairs</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center bg-gray-800 p-6 rounded-lg"
            >
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-3">BBB Accredited</h3>
              <p className="text-gray-300">Proud member of the Better Business Bureau with an A+ rating</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;